import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "./Offers.css";

const siteUrl = "https://www.samsoftix.com";

const Offers = () => {
  const navigate = useNavigate();

  const handleBookClick = () => {
    navigate("/serviceBooking");
  };

  const offers = [
    {
      icon: "🎁",
      title: "Free 30-Minute IT Consultation",
      description:
        "Discuss your website, mobile app, software, ecommerce, AI or digital business requirements with the SamSoftix team before starting your project.",
      tag: "FREE CONSULTATION",
      action: "Book Consultation",
      primary: true,
    },
    {
      icon: "💻",
      title: "30% Off Online Service Booking",
      description:
        "Book an eligible SamSoftix IT service or training session online and receive the advertised 30% booking discount.",
      tag: "LIMITED OFFER",
      action: "Book Now",
      primary: true,
    },
    {
      icon: "🌐",
      title: "Business Website Development",
      description:
        "Professional responsive websites for startups, local businesses, companies and service providers, with modern design and an SEO-ready technical foundation.",
      tag: "WEB DEVELOPMENT",
      action: "Discuss Website",
    },
    {
      icon: "⚛️",
      title: "React & Next.js Development",
      description:
        "Modern web applications, business portals and custom platforms using React and Next.js with responsive interfaces and scalable architecture.",
      tag: "WEB APPLICATIONS",
      action: "Start a Web Project",
    },
    {
      icon: "🛒",
      title: "Ecommerce Website Solutions",
      description:
        "Build or improve your online store with Shopify, WooCommerce or custom ecommerce development, including product structure, responsive design and technical setup.",
      tag: "ECOMMERCE",
      action: "Discuss Ecommerce",
    },
    {
      icon: "📱",
      title: "Mobile App Development",
      description:
        "Android and iOS applications using Flutter or React Native for startups, businesses, schools and organizations.",
      tag: "MOBILE APPS",
      action: "Discuss Mobile App",
    },
    {
      icon: "🤖",
      title: "AI & Automation Solutions",
      description:
        "Explore practical AI integrations, intelligent application features, automation workflows, data processing and machine-learning solutions for business use cases.",
      tag: "AI SOLUTIONS",
      action: "Discuss AI Project",
    },
    {
      icon: "🔍",
      title: "SEO & Website Optimization",
      description:
        "Improve your website's technical SEO foundation, content structure, performance and search visibility with practical optimization work.",
      tag: "SEO",
      action: "Request SEO Help",
    },
    {
      icon: "📍",
      title: "Local SEO & Google Business Support",
      description:
        "Improve your local online presence with website optimization, local business content and practical guidance for businesses targeting customers in their service area.",
      tag: "LOCAL SEO",
      action: "Discuss Local SEO",
    },
    {
      icon: "⚡",
      title: "Website Speed & Performance",
      description:
        "Identify common website performance problems and improve loading experience, mobile usability and technical implementation where possible.",
      tag: "PERFORMANCE",
      action: "Request Audit",
    },
    {
      icon: "☁️",
      title: "Hosting, Domain & Deployment Setup",
      description:
        "Get help with domain configuration, hosting setup, SSL, website deployment, DNS configuration and technical launch requirements.",
      tag: "DEPLOYMENT",
      action: "Get Deployment Help",
    },
    {
      icon: "🛠️",
      title: "Website Maintenance & Support",
      description:
        "Ongoing technical support for website updates, troubleshooting, content changes, integrations, deployment and routine improvements.",
      tag: "SUPPORT",
      action: "Request Support",
    },
    {
      icon: "🎨",
      title: "UI/UX & Website Design",
      description:
        "User-focused interfaces for websites and digital products with responsive layouts, clear navigation and modern visual presentation.",
      tag: "UI/UX",
      action: "Discuss Design",
    },
    {
      icon: "🎓",
      title: "Student Project Development & Guidance",
      description:
        "Practical development guidance for students working on websites, mobile applications, software projects and final-year technology projects.",
      tag: "STUDENT SUPPORT",
      action: "Ask About Projects",
    },
    {
      icon: "👨‍💻",
      title: "Web Development Training",
      description:
        "Learn HTML, CSS, JavaScript, React and modern web development through practical exercises and project-based learning.",
      tag: "TRAINING",
      action: "Ask About Training",
    },
    {
      icon: "📲",
      title: "Mobile App Development Training",
      description:
        "Learn Flutter and mobile application development through practical projects, APIs, Firebase integration, testing and publishing guidance.",
      tag: "TRAINING",
      action: "Ask About Training",
    },
    {
      icon: "🎮",
      title: "Game Development Training",
      description:
        "Learn game development fundamentals and build practical projects using suitable game development technologies.",
      tag: "TRAINING",
      action: "Ask About Training",
    },
    {
      icon: "🐍",
      title: "Python, AI & Machine Learning Training",
      description:
        "Build foundational programming, data and machine-learning skills through practical exercises and small projects.",
      tag: "TRAINING",
      action: "Ask About Training",
    },
    {
      icon: "🏫",
      title: "School & College IT Solutions",
      description:
        "Technology support for educational institutions including computer lab setup, software installation, systems and practical IT requirements.",
      tag: "EDUCATION",
      action: "Discuss Institution Needs",
    },
    {
      icon: "💼",
      title: "Business Software & Custom Systems",
      description:
        "Discuss CRM, management systems, booking platforms, dashboards, internal tools and other custom software requirements.",
      tag: "CUSTOM SOFTWARE",
      action: "Discuss Software",
    },
    {
      icon: "💬",
      title: "WhatsApp Business & Digital Setup",
      description:
        "Improve customer communication with practical WhatsApp Business, website contact and digital communication setup guidance.",
      tag: "BUSINESS DIGITALIZATION",
      action: "Discuss Setup",
    },
    {
      icon: "🌍",
      title: "International Remote Development",
      description:
        "SamSoftix works remotely with businesses and organizations outside Pakistan for websites, applications, ecommerce, AI and custom software projects.",
      tag: "GLOBAL SERVICES",
      action: "Start International Project",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `${siteUrl}/#business`,
        name: "SamSoftix",
        url: `${siteUrl}/`,
        telephone: "+92-332-0472184",
        email: "info@samsoftix.com",
        description:
          "SamSoftix is a software development company based in Wan, Sambrial, Punjab, Pakistan, providing website development, mobile app development, ecommerce, AI solutions, custom software, SEO, hosting and technology training.",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Wan, Sambrial",
          addressLocality: "Wan",
          addressRegion: "Punjab",
          postalCode: "51010",
          addressCountry: "PK",
        },
        areaServed: [
          {
            "@type": "City",
            name: "Wan",
          },
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "AdministrativeArea",
            name: "Punjab",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
          {
            "@type": "Country",
            name: "United Arab Emirates",
          },
          {
            "@type": "Country",
            name: "Saudi Arabia",
          },
          {
            "@type": "Country",
            name: "United States",
          },
          {
            "@type": "Country",
            name: "United Kingdom",
          },
        ],
        knowsAbout: [
          "Website Development",
          "Web Application Development",
          "React Development",
          "Next.js Development",
          "Mobile App Development",
          "Flutter Development",
          "React Native Development",
          "AI Development",
          "Machine Learning",
          "Ecommerce Development",
          "Custom Software Development",
          "SEO",
          "Local SEO",
          "Website Performance",
          "Technology Training",
        ],
        sameAs: [
          "https://facebook.com/samsoftix",
          "https://linkedin.com/company/samsoftix",
          "https://instagram.com/samsoftix",
        ],
      },

      {
        "@type": "OfferCatalog",
        "@id": `${siteUrl}/offers#offer-catalog`,
        name: "SamSoftix IT, Software Development & Training Offers",
        url: `${siteUrl}/offers`,
        description:
          "Current SamSoftix software development, IT services, digital solutions and technology training offers.",
        itemListElement: offers.map((offer, index) => ({
          "@type": "Offer",
          position: index + 1,
          name: offer.title,
          description: offer.description,
          url: `${siteUrl}/offers`,
        })),
      },

      {
        "@type": "WebPage",
        "@id": `${siteUrl}/offers#webpage`,
        url: `${siteUrl}/offers`,
        name: "IT, Software Development & Training Offers | SamSoftix",
        description:
          "Explore SamSoftix offers for website development, mobile apps, ecommerce, AI solutions, SEO, custom software, hosting, IT support and technology training.",
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#business`,
        },
        inLanguage: "en-PK",
      },

      {
        "@type": "BreadcrumbList",
        "@id": `${siteUrl}/offers#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${siteUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Offers",
            item: `${siteUrl}/offers`,
          },
        ],
      },
    ],
  };

  return (
    <div className="offers-page">
      <Helmet>
        <html lang="en-PK" />

        <title>
          IT & Software Development Offers | SamSoftix Pakistan
        </title>

        <meta
          name="description"
          content="Explore SamSoftix offers for website development, mobile apps, ecommerce, AI solutions, SEO, custom software, hosting, IT support and technology training in Pakistan and for international clients."
        />

        <meta
          name="keywords"
          content="SamSoftix offers, software development offers Pakistan, IT services Pakistan, website development Pakistan, web development Sambrial, software house Sambrial, software company Sialkot, website development Sialkot, web application development Pakistan, React developer Pakistan, Next.js development Pakistan, mobile app development Pakistan, Flutter development Pakistan, ecommerce development Pakistan, Shopify development Pakistan, WooCommerce development Pakistan, AI development Pakistan, custom software development Pakistan, SEO services Pakistan, local SEO Sambrial, local SEO Sialkot, website maintenance Pakistan, website speed optimization Pakistan, hosting setup Pakistan, domain setup Pakistan, IT support Sambrial, student project development Pakistan, web development training Sambrial, mobile app training Pakistan, AI ML training Pakistan, game development training Pakistan, school lab setup Pakistan, software solutions for businesses Pakistan"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta
          name="author"
          content="SamSoftix"
        />

        <meta
          name="theme-color"
          content="#06111f"
        />

        <meta
          name="geo.region"
          content="PK-PB"
        />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <link
          rel="canonical"
          href={`${siteUrl}/offers`}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content="IT & Software Development Offers | SamSoftix"
        />

        <meta
          property="og:description"
          content="Website development, mobile apps, ecommerce, AI, SEO, custom software, IT support and technology training offers from SamSoftix."
        />

        <meta
          property="og:url"
          content={`${siteUrl}/offers`}
        />

        <meta
          property="og:site_name"
          content="SamSoftix"
        />

        <meta
          property="og:locale"
          content="en_PK"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="IT & Software Development Offers | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Explore SamSoftix offers for web development, mobile apps, ecommerce, AI, SEO, custom software and technology training."
        />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
          ===================================================== */}

      <section className="offers-hero">
        <div className="offers-hero-badge">
          SAMSOFTIX · IT & DIGITAL SOLUTIONS
        </div>

        <h1>
          Software Development, IT & Training Offers
        </h1>

        <p>
          Explore current SamSoftix offers for{" "}
          <strong>
            website development, mobile apps, ecommerce,
            AI solutions, custom software, SEO, hosting,
            IT support and technology training.
          </strong>{" "}
          We work with businesses, startups, educational
          institutions and students in Pakistan and with
          international clients through remote delivery.
        </p>

        <div className="offers-hero-actions">
          <button
            type="button"
            className="offers-primary-btn"
            onClick={handleBookClick}
          >
            Book a Service →
          </button>

          <a
            href="#all-offers"
            className="offers-secondary-btn"
          >
            Explore Offers
          </a>
        </div>

        <div className="offers-location-strip">
          <span>📍 Sambrial</span>
          <span>📍 Sialkot</span>
          <span>🇵🇰 Pakistan</span>
          <span>🌍 International Remote Services</span>
        </div>
      </section>

      {/* =====================================================
          INTRO
          ===================================================== */}

      <section className="offers-intro">
        <div className="offers-container">
          <div className="offers-intro-grid">
            <div>
              <span className="offers-kicker">
                WHAT WE OFFER
              </span>

              <h2>
                Technology services built around your
                actual business or learning needs.
              </h2>
            </div>

            <p>
              Whether you need a business website, mobile
              application, online store, custom software,
              AI integration, SEO support or practical
              technology training, SamSoftix can discuss
              the requirements and recommend an appropriate
              development approach.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          OFFERS
          ===================================================== */}

      <section
        className="offers-list-section"
        id="all-offers"
      >
        <div className="offers-container">
          <div className="offers-section-heading">
            <span className="offers-kicker">
              SERVICES & SPECIAL OFFERS
            </span>

            <h2>
              Explore SamSoftix
              <span> services and offers.</span>
            </h2>

            <p>
              Choose a service below or contact us if your
              requirement is not listed.
            </p>
          </div>

          <div className="offers-list">
            {offers.map((offer, index) => (
              <article
                className={`offer-box ${
                  offer.primary
                    ? "highlight-offer"
                    : ""
                }`}
                key={offer.title}
              >
                <div className="offer-card-top">
                  <span className="offer-icon">
                    {offer.icon}
                  </span>

                  <span className="offer-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <span className="offer-tag">
                  {offer.tag}
                </span>

                <h3>{offer.title}</h3>

                <p>{offer.description}</p>

                <button
                  type="button"
                  className="offer-card-btn"
                  onClick={handleBookClick}
                >
                  {offer.action} →
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          HOSTING PROMOTION
          ===================================================== */}

      <section className="hosting-promo">
        <div className="offers-container">
          <div className="hosting-promo-card">
            <div className="hosting-promo-content">
              <span className="offers-kicker">
                HOSTING PROMOTION
              </span>

              <h2>
                20% Off Hostinger International Hosting
              </h2>

              <p>
                If you need hosting for a new website,
                ecommerce store or web application, use the
                SamSoftix referral promotion for eligible
                Hostinger services.
              </p>

              <div className="promo-code-box">
                <span>Referral Code</span>

                <strong className="promo-code">
                  E2CQASIMC7DN
                </strong>
              </div>

              <a
                className="cta-btn"
                href="https://hostinger.com?REFERRALCODE=E2CQASIMC7DN"
                target="_blank"
                rel="noopener noreferrer sponsored"
              >
                Claim Hosting Offer ↗
              </a>
            </div>

            <div className="hosting-promo-side">
              <span>WEB</span>
              <strong>HOSTING</strong>
              <small>
                Domain · SSL · Deployment
              </small>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          LOCAL + INTERNATIONAL
          ===================================================== */}

      <section className="service-area-section">
        <div className="offers-container">
          <div className="service-area-card">
            <span className="offers-kicker">
              LOCAL + INTERNATIONAL
            </span>

            <h2>
              SamSoftix serves businesses in Pakistan
              and remote clients internationally.
            </h2>

            <p>
              Our local service area includes Wan, Sambrial,
              Sialkot and surrounding areas of Punjab.
              Remote development and consultation can also
              be provided for clients across Pakistan and
              international markets.
            </p>

            <div className="service-area-tags">
              <span>Wan</span>
              <span>Sambrial</span>
              <span>Sialkot</span>
              <span>Punjab</span>
              <span>Pakistan</span>
              <span>UAE</span>
              <span>Saudi Arabia</span>
              <span>USA</span>
              <span>UK</span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
          ===================================================== */}

      <section className="offer-cta">
        <div className="offers-container">
          <span className="offers-kicker">
            START YOUR PROJECT
          </span>

          <h2>
            Have a project or learning goal?
          </h2>

          <p>
            Tell SamSoftix what you need. Whether it is a
            website, mobile application, ecommerce store,
            AI solution, custom software project, SEO
            requirement or technology course, we can discuss
            the next step with you.
          </p>

          <div className="offer-cta-actions">
            <button
              type="button"
              onClick={handleBookClick}
            >
              Book Now →
            </button>

            <a
              href="https://wa.me/923320472184"
              target="_blank"
              rel="noopener noreferrer"
              className="offer-whatsapp-btn"
            >
              WhatsApp Us
            </a>
          </div>

          <div className="offer-contact-line">
            +92 332 0472184 · info@samsoftix.com
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offers;